import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../styles/admin/admin-table.css";
import "../../styles/admin/admin-form.css";
import config from "../../config";

const API_BASE_URL = String(config.apiBaseUrl || "").replace(/\/+$/, "");

export default function AdminPets() {
  const navigate = useNavigate();

  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [especie, setEspecie] = useState("TODAS");
  const [sexo, setSexo] = useState("TODOS");
  const [estadoPropietario, setEstadoPropietario] = useState("TODOS");

  useEffect(() => {
    loadPets();
  }, []);

  const loadPets = async () => {
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("No hay sesión activa. Inicia sesión nuevamente.");
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/pets`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(responseText || "No se pudo cargar la lista de mascotas");
      }

      const data = responseText ? JSON.parse(responseText) : [];
      setPets(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Ocurrió un error al cargar las mascotas");
      setPets([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleDateString("es-CO");
    } catch {
      return "-";
    }
  };

  const normalizeSpecies = (value) => {
    const v = (value || "").toLowerCase();
    if (v === "perro") return "Perro";
    if (v === "gato") return "Gato";
    return value || "-";
  };

  const normalizeSex = (value) => {
    const v = (value || "").toLowerCase();
    if (v === "macho") return "Macho";
    if (v === "hembra") return "Hembra";
    return value || "-";
  };

  const filteredPets = useMemo(() => {
    return pets.filter((pet) => {
      const fullOwnerName = `${pet.ownerName || ""} ${pet.ownerLastName || ""}`.trim().toLowerCase();
      const searchValue = search.toLowerCase().trim();

      const matchesSearch =
        !searchValue ||
        (pet.name || "").toLowerCase().includes(searchValue) ||
        fullOwnerName.includes(searchValue) ||
        (pet.ownerEmail || "").toLowerCase().includes(searchValue) ||
        (pet.breed || "").toLowerCase().includes(searchValue);

      const petSpecies = normalizeSpecies(pet.species);
      const petSex = normalizeSex(pet.sex);
      const ownerStatus = (pet.ownerStatus || "").toUpperCase();

      const matchesEspecie = especie === "TODAS" || petSpecies === especie;
      const matchesSexo = sexo === "TODOS" || petSex === sexo;
      const matchesEstado = estadoPropietario === "TODOS" || ownerStatus === estadoPropietario;

      return matchesSearch && matchesEspecie && matchesSexo && matchesEstado;
    });
  }, [pets, search, especie, sexo, estadoPropietario]);

  return (
    <div className="admin-page">
      <div className="table-page-header">
        <div>
          <h2>Mascotas</h2>
        </div>
      </div>

      <div className="table-toolbar">
        <input
          className="table-search"
          type="text"
          placeholder="Buscar por nombre, propietario, correo o raza"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="table-filters">
          <select value={especie} onChange={(e) => setEspecie(e.target.value)}>
            <option value="TODAS">Todas las especies</option>
            <option value="Perro">Perro</option>
            <option value="Gato">Gato</option>
          </select>

          <select value={sexo} onChange={(e) => setSexo(e.target.value)}>
            <option value="TODOS">Todos los sexos</option>
            <option value="Macho">Macho</option>
            <option value="Hembra">Hembra</option>
          </select>

          <button type="button" className="secondary-btn" onClick={loadPets}>
            Recargar
          </button>
        </div>
      </div>

      {error ? <p className="admin-form-message error">{error}</p> : null}

      <div className="table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID mascota</th>
              <th>Nombre</th>
              <th>Especie</th>
              <th>Raza</th>
              <th>Sexo</th>
              <th>Fecha de nacimiento</th>
              <th>Propietario</th>
              <th>Correo del propietario</th>
              <th>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="empty-row">
                  Cargando mascotas...
                </td>
              </tr>
            ) : filteredPets.length > 0 ? (
              filteredPets.map((pet) => {
                const fullOwnerName = `${pet.ownerName || ""} ${pet.ownerLastName || ""}`.trim();

                return (
                  <tr key={pet.idPet}>
                    <td>{pet.idPet}</td>
                    <td>{pet.name || "-"}</td>
                    <td>{normalizeSpecies(pet.species)}</td>
                    <td>{pet.breed || "-"}</td>
                    <td>{normalizeSex(pet.sex)}</td>
                    <td>{formatDate(pet.birthDate)}</td>
                    <td>{fullOwnerName || "-"}</td>
                    <td>{pet.ownerEmail || "-"}</td>
                    <td>
                      <div className="table-actions">
                        <button onClick={() => navigate(`/admin/mascotas/${pet.idPet}`)}>
                          Ver
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="9" className="empty-row">
                  No se encontraron mascotas.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}